import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DiscountLabelModule } from '@epicuro-next/ataraxia/discount-label';
import { ImageModule, IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import { IFilter } from '@epicuro-next/ataraxia/models';
import { ProductCardTitleComponent } from '@epicuro-next/ataraxia/product-card';
import { ProductPriceModule } from '@epicuro-next/ataraxia/product-price';
import {
  ProductIdPipe,
  ProductTitleModule,
} from '@epicuro-next/ataraxia/product-title';
import { filterLabelsById } from '@epicuro-next/constants/features/localization';
import { FilterDisplayNamePipe } from '@epicuro-next/features/products-filters';
import {
  selectActiveFilters,
  withPcardSettingStoreFeature,
} from '@epicuro-next/state/p-basket-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { signalStore } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import {
  FilterLabelsByNamePipe,
  ProductLabelLocation,
  ProductLabelsComponent,
} from 'ataraxia-product-labels';
import {
  Product,
  SearchSuggestion,
  SuggestProduct,
} from 'graphql-api/gql-api-interfaces';

// tslint:disable-next-line:variable-name
const PCardSettingStore = signalStore(withPcardSettingStoreFeature());

@Component({
  selector: 'epc-search-suggest',
  imports: [
    CommonModule,
    ImageModule,
    ProductPriceModule,
    FilterDisplayNamePipe,
    ProductIdPipe,
    ProductCardTitleComponent,
    ProductTitleModule,
    ProductLabelsComponent,
    DiscountLabelModule,
    FilterLabelsByNamePipe,
  ],
  templateUrl: './search-suggest.component.html',
  styleUrl: './search-suggest.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PCardSettingStore],
})
export class SearchSuggestComponent {
  private store = inject(Store);
  private readonly pcardSettingsStore = inject(PCardSettingStore);
  public products = input<SuggestProduct[]>([]);
  public filters = input<SearchSuggestion[]>([]);
  public autocompletes = input<SearchSuggestion[]>([]);
  public onAutocompleteClick = output<string>();
  public filterItemClick = output<{
    filter: IFilter;
    isActive: boolean;
  }>();
  public productClick = output<Product>();
  public filterLabels = filterLabelsById;

  public readonly labelsLocation = ProductLabelLocation.All;

  public readonly autocompletesFlattened = computed(() => {
    return this.autocompletes().flatMap(
      (a) => a.options?.map((o) => o.text) ?? [],
    );
  });

  public readonly hasAutocomplete = computed(
    () => this.autocompletesFlattened()?.length > 0,
  );

  private activeFilters = toSignal(this.store.select(selectActiveFilters));

  public mappedFilters = computed(() => {
    const activeFilters = this.activeFilters();
    return this.filters()?.map((filter) => {
      return {
        ...filter,
        fieldId: filter.filterField,
        displayName: filter.filterField,
        options: filter.options?.map((option) => ({
          ...option,
          isActive: isNonNullish(
            activeFilters?.find(
              (activeFilter) =>
                activeFilter.fieldId === filter.filterField &&
                activeFilter.value === option.text,
            ),
          ),
        })),
      };
    });
  });

  public readonly hasFilters = computed(() => this.filters()?.length > 0);

  public imageMaxSize = IMAGE_MAX_SIZES.AVATAR;

  public readonly showWspdPrice = this.pcardSettingsStore.showWspdPrice;
  public readonly showWholeSalePrices =
    this.pcardSettingsStore.showWholeSalePrices;
  public readonly showMarkupPrice = this.pcardSettingsStore.showMarkupPrice;
  public readonly productIdAdditionalPrefixRexExp =
    this.pcardSettingsStore.productIdAdditionalPrefixRexExp;
  public readonly titleFields = this.pcardSettingsStore.titleFields;
  public readonly idFields = this.pcardSettingsStore.idFields;

  public onProductClick(product: SuggestProduct) {
    this.productClick.emit({
      ...product,
      parent: {
        productId: product.parentId,
      },
    } as Product);
  }

  public onFilterItemClick(value: string, fieldId: string, isActive: boolean) {
    this.filterItemClick.emit({
      filter: {
        fieldId,
        value,
      },
      isActive,
    });
  }
}
